import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { checkEmail, sendVerificationCode, verifyCode, checkNickname, signupWithEmail } from '../api/authApi';

const Signup: React.FC = () => {
  const [step, setStep] = useState(1); // 현재 단계
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [phone, setPhone] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [nickname, setNickname] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const [isEmailChecked, setIsEmailChecked] = useState(false);
  const [isCodeVerified, setIsCodeVerified] = useState(false);
  const [isNicknameChecked, setIsNicknameChecked] = useState(false);

  const handleEmailCheck = async () => {
    setLoading(true);
    try {
      await checkEmail(email);
      alert('이메일이 확인되었습니다.');
      setIsEmailChecked(true); // ✅ 성공 시 true
    } catch (err: any) {
      const errorMessage = '해당 이메일로 가입한 유저가 이미 존재합니다.';
      alert(errorMessage);
      setIsEmailChecked(false); // ✅ 실패 시 false
    } finally {
      setLoading(false);
    }
  };

  const handleSendVerificationCode = async () => {
    setLoading(true);
    try {
      await sendVerificationCode(phone, 1, "######");
      alert('인증번호가 발송되었습니다.');
    } catch (err) {
      setError('인증번호 발송에 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyCode = async () => {
    setLoading(true);
    try {
      await verifyCode(phone, verificationCode);
      alert('인증번호가 확인되었습니다.');
      setIsCodeVerified(true); // ✅ 성공 시 true
    } catch (err) {
      setError('인증번호 확인에 실패했습니다.');
      setIsCodeVerified(false); // ✅ 실패 시 false
    } finally {
      setLoading(false);
    }
  };

  const handleNicknameCheck = async () => {
    setLoading(true);
    try {
      await checkNickname(nickname);
      alert('사용 가능한 닉네임입니다.');
      setIsNicknameChecked(true); // ✅ 성공 시 true
    } catch (err) {
      setError('닉네임 중복 확인에 실패했습니다.');
      setIsNicknameChecked(false); // ✅ 실패 시 false
    } finally {
      setLoading(false);
    }
  };

  const handleNext = () => {
    if (step === 1 && !email) {
      setError('이메일을 입력해주세요.');
      return;
    }
    if (step === 2 && (password !== confirmPassword || !password)) {
      setError('비밀번호가 일치하지 않습니다.');
      return;
    }
    if (step === 3 && (!phone || !verificationCode)) {
      setError('인증번호를 입력해주세요.');
      return;
    }
    if (step === 4 && !nickname) {
      setError('닉네임을 입력해주세요.');
      return;
    }
    setError('');
    setStep((prev) => prev + 1);
  };

  const handlePrevious = () => {
    setError('');
    setStep((prev) => prev - 1);
  };

  const handleSubmit = async () => {
    if (!nickname) {
      setError('닉네임을 입력해주세요.');
      return;
    }
    if (!email || !password) {
      setError('이메일 또는 비밀번호를 입력해주세요.');
      return;
    }

    setError('');
    setLoading(true);

    try {
      // signupWithEmail 호출
      await signupWithEmail(email, password, nickname, phone);
      alert('회원가입 완료!');
      navigate('/login');  // 로그인 페이지로 이동
    } catch (err) {
      setError('회원가입 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };

  // 각 단계별 "다음" 버튼 활성화 여부 판단
  const isNextButtonEnabled = () => {
    if (step === 1) {
      return email && isEmailChecked && !loading; // ✅ 이메일 확인 성공 시
    }
    if (step === 2) {
      return password && confirmPassword && password === confirmPassword && !loading;
    }
    if (step === 3) {
      return phone && verificationCode && isCodeVerified && !loading; // ✅ 인증번호 확인 성공 시
    }
    if (step === 4) {
      return nickname && isNicknameChecked && !loading; // ✅ 닉네임 중복 확인 성공 시
    }
    return false;
  };

  return (
    <div className="w-screen h-screen flex items-center justify-center bg-gray-100">
      <div className="w-full max-w-sm">
        <form
          className="bg-white shadow-lg rounded-lg px-8 py-6 flex flex-col"
          style={{ height: '450px' }}
        >
          <div className="w-full mb-6">
            <div className="h-1 w-full bg-gray-200 rounded-full mt-1">
              <div
                className="h-full bg-black rounded-full"
                style={{ width: `${(step / 4) * 100}%` }}
              ></div>
            </div>
          </div>

          <h2 className="text-2xl font-semibold mb-6">간편 회원가입</h2>

          {error && <p className="text-red-500 mb-4">{error}</p>}

          <div className="flex-grow">
            {step === 1 && (
              <>
                <input
                  type="email"
                  className="mb-3 px-4 py-2 border rounded w-full"
                  placeholder="이메일을 입력하세요."
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
                <button
                  type="button"
                  className="bg-blue-500 hover:bg-blue-600 text-white py-2 w-full rounded font-semibold"
                  onClick={handleEmailCheck}
                  disabled={loading}
                >
                  {loading ? '확인 중...' : '이메일 확인'}
                </button>
              </>
            )}

            {step === 2 && (
              <>
                <input
                  type="password"
                  className="mb-3 px-4 py-2 border rounded w-full"
                  placeholder="비밀번호를 입력하세요."
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
                <input
                  type="password"
                  className="mb-3 px-4 py-2 border rounded w-full"
                  placeholder="비밀번호를 입력하세요."
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                />
              </>
            )}

            {step === 3 && (
              <>
                <input
                  type="text"
                  className="mb-3 px-4 py-2 border rounded w-full"
                  placeholder="휴대폰 번호를 입력하세요."
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  required
                />
                <p className="text-xs text-gray-500 mb-3">- 없이 숫자만 입력해주세요. (예: 01012345678)</p>

                <button
                  type="button"
                  className="bg-blue-500 hover:bg-blue-600 text-white py-2 w-full rounded font-semibold mb-3"
                  onClick={handleSendVerificationCode}
                  disabled={loading}
                >
                  {loading ? '발송 중...' : '인증번호 발송'}
                </button>
                <input
                  type="text"
                  className="mb-3 px-4 py-2 border rounded w-full"
                  placeholder="인증번호 입력"
                  value={verificationCode}
                  onChange={(e) => setVerificationCode(e.target.value)}
                  required
                />
                <button
                  type="button"
                  className="bg-blue-500 hover:bg-blue-600 text-white py-2 w-full rounded font-semibold"
                  onClick={handleVerifyCode}
                  disabled={loading}
                >
                  {loading ? '확인 중...' : '인증번호 확인'}
                </button>
              </>
            )}

            {step === 4 && (
              <>
                <input
                  type="text"
                  className="mb-3 px-4 py-2 border rounded w-full"
                  placeholder="닉네임을 입력하세요."
                  value={nickname}
                  onChange={(e) => setNickname(e.target.value)}
                  required
                />
                <button
                  type="button"
                  className="bg-blue-500 hover:bg-blue-600 text-white py-2 w-full rounded font-semibold"
                  onClick={handleNicknameCheck}
                  disabled={loading}
                >
                {loading ? '확인 중...' : '중복 확인'}
                </button>
              </>
            )}
          </div>

          <div className="flex justify-end w-full mt-4">
            {step > 1 && (
              <button
                type="button"
                className="bg-gray-500 hover:bg-gray-600 text-white py-2 px-4 rounded font-semibold mr-3"
                onClick={handlePrevious}
              >
                이전
              </button>
            )}
            {step < 4 && (
              <button
                type="button"
                className={`bg-green-500 hover:bg-green-600 text-white py-2 px-4 rounded font-semibold ${!isNextButtonEnabled() && 'opacity-50 cursor-not-allowed'}`}
                onClick={handleNext}
                disabled={!isNextButtonEnabled()}
              >
                다음
              </button>
            )}
            {step === 4 && (
              <button
                type="button"
                className="bg-green-500 hover:bg-green-600 text-white py-2 px-4 rounded font-semibold"
                onClick={handleSubmit}
              >
                완료
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

export default Signup;
